import { useCallback, useRef } from 'react';
import { apiErrorCode, isNetworkError } from './apiClient';

/**
 * The lifecycle of one business intent's idempotency key.
 *
 * A key identifies *what the cashier is trying to do*, not the button press. Ringing up this
 * basket is one intent; taking a later payment against the same order is another, and they must
 * never share a key — the server treats a key used on a second endpoint as a reuse and refuses it.
 *
 * When to hold on to a key is the whole subtlety:
 *
 *   - a timeout or a dropped connection means we do not know whether the server acted, so the same
 *     key must go back — that is the case idempotency exists for;
 *   - `REQUEST_IN_PROGRESS` means our own earlier attempt is still running, so the same key again;
 *   - success ends the intent;
 *   - a business rejection ends the attempt. The next try mints a fresh key, so a basket refused
 *     for stock is genuinely re-evaluated once the shelf is restocked rather than replaying the
 *     refusal.
 *
 * The body is fingerprinted too. If it changed, this is a different operation and gets a new key
 * before the server has to tell us so.
 */
export interface OperationKey {
  /** The key to send, minting or reusing as the situation requires. */
  next: (body: unknown) => string;
  /** Call after a settled attempt so the key is kept or discarded correctly. */
  settle: (outcome: 'SUCCESS' | 'RETRYABLE' | 'REJECTED') => void;
  /** Classifies a caught error into the outcome above. */
  classify: (error: unknown) => 'RETRYABLE' | 'REJECTED';
  /** Drops the key outright — abandoning the basket, or logging out. */
  reset: () => void;
}

export function useOperationKey(): OperationKey {
  const held = useRef<{ key: string; fingerprint: string } | null>(null);

  const next = useCallback((body: unknown) => {
    const fingerprint = stableStringify(body);

    // A changed payload is a different operation, whatever the last attempt was doing.
    if (!held.current || held.current.fingerprint !== fingerprint) {
      held.current = { key: crypto.randomUUID(), fingerprint };
    }

    return held.current.key;
  }, []);

  const settle = useCallback((outcome: 'SUCCESS' | 'RETRYABLE' | 'REJECTED') => {
    if (outcome === 'RETRYABLE') return;
    held.current = null;
  }, []);

  const classify = useCallback((error: unknown): 'RETRYABLE' | 'REJECTED' => {
    // We never learned what the server did, so the key has to go back with the retry.
    if (isNetworkError(error)) return 'RETRYABLE';
    return apiErrorCode(error) === 'REQUEST_IN_PROGRESS' ? 'RETRYABLE' : 'REJECTED';
  }, []);

  const reset = useCallback(() => {
    held.current = null;
  }, []);

  return { next, settle, classify, reset };
}

/**
 * A stable string for a request body.
 *
 * Mirrors the server's canonicalisation — keys sorted at every level, `undefined` dropped — so the
 * client's idea of "same operation" and the server's cannot disagree and produce a spurious
 * key-reuse refusal.
 */
export function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`);

    return `{${entries.join(',')}}`;
  }

  return JSON.stringify(value);
}
